<script setup>
import FormInputLabel from './Label';
import FormInputError from './Error';
import VueMultiSelect from 'vue-multiselect';
import {ref} from 'vue';
import {usePage} from '@inertiajs/vue3';
import {useLocales} from '@/composables/locales';
import HoverIcon from '@/components/Shared/HoverIcon.vue';
import FormDateTimePicker from '@/components/Shared/Form/DateTimePicker/Index.vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object],
    default: '',
  },
  labelText: {
    type: String,
    default: '',
  },
  helpText: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  containerClass: {
    type: String,
    default: '',
  },
  translatable: {
    type: Boolean,
    default: false,
  },
  hoverHelpText: {
    type: String,
    default: null,
  },
});

const page = usePage();

const locale = ref('en');
const locales = useLocales().locales;
</script>

<template>
  <div :class="containerClass">
    <template v-if="$attrs.type === 'multi-select' || $attrs.type === 'select'">
      <form-input-label v-if="labelText" :for="$attrs.id" :required="$attrs.required" :value="labelText">
        <span v-if="hoverHelpText" class="px-2">
          <hover-icon :title="hoverHelpText" hover icon="question-mark-circle"/>
        </span>
      </form-input-label>

      <vue-multi-select
        :multiple="$attrs.type === 'multi-select'"
        :options="$attrs.options ?? []"
        v-bind="$attrs"
        v-bind:modelValue="modelValue"
        @update:modelValue="value => $emit('update:modelValue', value)"
      />

      <input :class="{ 'is-invalid': error }" class="form-control" type="hidden"/>
    </template>

    <template v-else-if="$attrs.type === 'checkbox'">
      <input
        ref="input"
        :checked="modelValue === true"
        :class="{ 'is-invalid': error }"
        class="form_knob_check"
        v-bind="$attrs"
        @input="$emit('update:modelValue', $event.target.checked)"
      />

      <form-input-label
        v-if="labelText"
        :for="$attrs.id"
        :required="$attrs.required"
        :value="labelText"
        class="form_knob text_normal mb-0"
      >
        <span v-if="hoverHelpText" class="px-2">
          <hover-icon :title="hoverHelpText" hover icon="question-mark-circle"/>
        </span>
      </form-input-label>
    </template>

    <template v-else-if="$attrs.type === 'textarea'">
      <form-input-label
        v-if="labelText"
        :class="{ 'form_label flex align-items-end': translatable, 'form_label': !translatable }"
        :for="$attrs.id"
        :required="$attrs.required"
        :value="labelText"
      >
        <span v-if="hoverHelpText" class="px-2">
          <hover-icon :title="hoverHelpText" hover icon="question-mark-circle"/>
        </span>
      </form-input-label>

      <textarea
        ref="input"
        :class="{ 'is-invalid': error }"
        :value="modelValue"
        class="form-control form_field"
        rows="5"
        v-bind="$attrs"
        @input="$emit('update:modelValue', $event.target.value)"
      >
      </textarea>
    </template>

    <template v-else-if="$attrs.type === 'date' || $attrs.type === 'datetime'">
      <form-input-label v-if="labelText" :for="$attrs.id" :required="$attrs.required" :value="labelText">
        <span v-if="hoverHelpText" class="px-2">
          <hover-icon :title="hoverHelpText" hover icon="question-mark-circle"/>
        </span>
      </form-input-label>

      <FormDateTimePicker
        :mode="$attrs.type === 'datetime' ? 'dateTime' : 'date'"
        :model-value="modelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
      />
    </template>

    <template v-else>
      <form-input-label
        v-if="labelText"
        :class="{ 'form_label flex align-items-end': translatable, 'form_label': !translatable }"
        :for="$attrs.id"
        :required="$attrs.required"
        :value="labelText"
      >
        <span v-if="hoverHelpText" class="px-2">
          <hover-icon :title="hoverHelpText" hover icon="question-mark-circle"/>
        </span>
      </form-input-label>

      <slot></slot>

      <input
        ref="input"
        :class="{ 'is-invalid': error }"
        class="form_field"
        v-bind="$attrs"
        v-bind:required="false"
        v-bind:value="modelValue"
        @input="$emit('update:modelValue', $event.target.value)"
      />
    </template>
    <p v-if="helpText" class="help-text">{{ helpText }}</p>
    <form-input-error v-if="error" :error-message="error"/>
  </div>
</template>

<script>
export default {
  inheritAttrs: false,
};
</script>
